import {
  prisma,
  Design,
  DesignTemplateTranslations,
  DesignTemplateType,
  TriggerEntityType,
  TriggerEvent,
  Prisma,
  HTMLTemplateType,
  RSVPvalues,
  DesignEntityType,
} from "client";
import { createTrigger } from "../trigger";
import {
  detachDesignFromMultipleBookableItems,
  getBookableItemIdsFromTicketIds,
  getBookableItemsByDesign,
} from "../bookableItem";

// CREATE

export const createDesignTemplate = async (
  template: Design,
  eventId: string,
  createdById: string,
  bookableItemIds?: string[],
  invitationsIds?: string[],
  translations?: DesignTemplateTranslations[],
  trigger?: TriggerEvent,
  HTMLType?: HTMLTemplateType
) => {
  try {
    // Fail fast with clearer messages than Prisma's connect error.
    if (eventId) {
      const eventExists = await prisma.event.findUnique({
        where: { id: eventId },
        select: { id: true },
      });
      if (!eventExists) {
        return { error: "Event not found" };
      }
    }

    let resolvedBookableItemIds = bookableItemIds ?? [];
    if (resolvedBookableItemIds.length) {
      const existing = await prisma.bookableItem.findMany({
        where: { id: { in: resolvedBookableItemIds } },
        select: { id: true },
      });

      // If the provided ids are not BookableItem ids, treat them as Ticket ids (legacy behavior)
      // and map them to BookableItem ids.
      if (existing.length !== resolvedBookableItemIds.length) {
        resolvedBookableItemIds = await getBookableItemIdsFromTicketIds(
          resolvedBookableItemIds
        );
      }

      if (!resolvedBookableItemIds.length) {
        return {
          error:
            "No matching BookableItems found for the provided linked ticket ids",
        };
      }
    }

    const previousTempWithSameName = await prisma.design.findFirst({
      where: {
        name: template.name,
        eventId,
        type: template.type,
      },
    });
    if (previousTempWithSameName) {
      return {
        error: "Template with same name already exists",
      };
    }
    const res = await prisma.design.create({
      data: {
        deletedAt: null,
        name: template.name,
        type: template.type,
        event: {
          connect: {
            id: eventId,
          },
        },
        ...(HTMLType && { HTMLType }),
        ...(translations
          ? {
              translations: {
                createMany: {
                  data: translations,
                },
              },
            }
          : {}),

        ...(resolvedBookableItemIds?.length
          ? {
              bookableItems: {
                connect: resolvedBookableItemIds.map((id) => ({ id })),
              },
            }
          : {}),
        invitations: invitationsIds?.length
          ? {
              createMany: {
                data: invitationsIds?.map((id) => ({
                  invitationId: id,
                })),
              },
            }
          : undefined,
      },
    });
    if (trigger) {
      await createTrigger(
        {
          triggerEvent: trigger,

          createdBy: {
            connect: { id: createdById },
          },
          createdAt: new Date(),
          updatedAt: new Date(),
        } as Prisma.TriggerCreateInput,
        TriggerEntityType.DESIGN,
        template.id,
        eventId
      );
    }
    return res;
  } catch (e: unknown) {
    console.error(e);
    return {
      error:
        e instanceof Error ? e.message : "Failed to create design template",
    };
  }
};
const htmlTemplateToRSVPMap: Partial<{
  [key in HTMLTemplateType]: RSVPvalues;
}> = {
  [HTMLTemplateType.YES_INVITATION_RESPONSE]: RSVPvalues.YES,
  [HTMLTemplateType.NO_INVITATION_RESPONSE]: RSVPvalues.NO,
  [HTMLTemplateType.MAYBE_INVITATION_RESPONSE]: RSVPvalues.MAYBE,
};

export const createRSVPDesignTemplate = async (
  template: Design,
  eventId: string,
  createdById: string,
  translations?: DesignTemplateTranslations[],
  invitationIds?: string[],
  entityType?: DesignEntityType,
  HTMLType?: HTMLTemplateType
) => {
  try {
    const res = await prisma.design.create({
      data: {
        deletedAt: null,
        name: template.name,
        type: template.type,
        entityType: entityType,
        HTMLType: HTMLType,
        createdBy: {
          connect: {
            id: createdById,
          },
        },
        event: {
          connect: {
            id: eventId,
          },
        },
        invitations: invitationIds?.length
          ? {
              createMany: {
                data: invitationIds?.map((id) => ({
                  invitationId: id,
                })),
              },
            }
          : undefined,
        ...(translations?.length
          ? {
              translations: {
                createMany: {
                  data: translations,
                },
              },
            }
          : {}),

      },
    });

    return res;
  } catch (e: any) {
    console.error("Error creating template:", e);
    return new Error(e?.message ?? "Error creating template");
  }
};
export const updateRSVPDesignTemplate = async (
  templateId: string,
  updatedData: Partial<Design>,
  translations?: DesignTemplateTranslations[],
  invitationIds?: string[],
  entityType?: DesignEntityType,
  HTMLType?: HTMLTemplateType
) => {
  try {
    const updateData: Prisma.DesignUpdateInput = {
      ...(updatedData.name && { name: updatedData.name }),
      ...(updatedData.type && { type: updatedData.type }),
      ...(HTMLType && { HTMLType: HTMLType }),
      ...(entityType && { entityType: entityType }),
    };

    if (translations && translations.length > 0) {
      updateData.translations = {
        deleteMany: {},
        createMany: {
          data: translations,
        },
      };
    }
    if (
      invitationIds &&
      invitationIds.length > 0 &&
      entityType === DesignEntityType.INVITATION &&
      HTMLType
    ) {
      const rsvpValue = htmlTemplateToRSVPMap[HTMLType];
      if (rsvpValue) {
        updateData.invitations = {
          connectOrCreate: invitationIds.map((id) => ({
            where: {
              invitationId_designId: {
                invitationId: id,
                designId: templateId,
              },
            },
            create: {
              invitation: { connect: { id } },
            },
          })),
        };
      }
    }
    // Only perform the update if there's data to update
    if (Object.keys(updateData).length > 0) {
      const res = await prisma.design.update({
        where: { id: templateId },
        data: updateData,
      });
      return res;
    } else {
      // If there's nothing to update, fetch and return the current design
      return await prisma.design.findUnique({
        where: { id: templateId },
      });
    }
  } catch (e: any) {
    console.error(e);
    throw new Error(e.message);
  }
};

// READ
/**
 * @deprecated Use getDesignsByBookableItem instead - this function is kept for backward compatibility
 * Get design template by entity ID (legacy function)
 */
export const getTicketTemplateByEntityId = async (
  id: string,
  types?: DesignTemplateType[],
  templateId?: string
) => {
  try {
    const query = {
      where: {
        deletedAt: null,
        ...(templateId
          ? { id: templateId } // If templateId exists, search by it only
          : {
              OR: [
                {
                  bookableItems: {
                    some: {
                      OR: [
                        {
                          ticket: {
                            id: id,
                          },
                        },
                        {
                          entityTier: {
                            entities: {
                              some: {
                                id: id,
                              },
                            },
                          },
                        },
                      ],
                    },
                  },
                },
                {
                  invitations: {
                    some: {
                      invitationId: id,
                    },
                  },
                },
              ],
              type: {
                in: types ?? [DesignTemplateType.DIGITAL_TICKET],
              },
            }),
      },
    };

    const res = await prisma.design.findFirst({
      where: query.where,
      select: {
        translations: {
          select: {
            ticketTemplateId: true,
            ticketTemplate: true,
            html: true,
            json: true,
            variables: true,
          },
        },
      },
    });

    return {
      ...res,
      template: res?.translations?.[0] ?? null,
    };
  } catch (e) {
    return e;
  }
};
/**
 * @deprecated Use getDesignsByBookableItem instead - this function is kept for backward compatibility
 * Get design templates by entity IDs (legacy function)
 */
export const getTicketTemplatesByEntityIds = async (
  ids: string[],
  type?: DesignTemplateType[]
) => {
  try {
    const res = await prisma.design.findMany({
      where: {
        deletedAt: null,
        bookableItems: {
          some: {
            OR: [
              {
                ticket: {
                  id: {
                    in: ids,
                  },
                },
              },
              {
                entityTier: {
                  entities: {
                    some: {
                      id: {
                        in: ids,
                      },
                    },
                  },
                },
              },
            ],
          },
        },
        type: {
          in: type ?? [DesignTemplateType.DIGITAL_TICKET],
        },
      },
      include: {
        translations: true,
        bookableItems: {
          include: {
            ticket: true,
            entityTier: {
              include: {
                entities: true,
              },
            },
          },
        },
      },
    });
    return res.map((item) => ({
      ...item,
      template: item?.translations?.[0]
        ? {
            ...item?.translations?.[0],
          }
        : null,
      ticketIds:
        item?.bookableItems
          ?.flatMap((bi) => [
            ...(bi.ticket ? [bi.ticket.id] : []),
            ...(bi.entityTier?.entities?.map((e) => e.id) || []),
          ])
          .filter((id) => ids.includes(id)) || [],
    }));
  } catch (e) {
    return e;
  }
};

export const getTemplateById = async (id: string) => {
  try {
    const res = await prisma.design.findUnique({
      where: {
        id,
      },
      include: {
        translations: true,
        invitations: true,
        triggerEntity: {
          include: {
            trigger: true,
          },
        },
        bookableItems: {
          include: {
            ticket: true,
            entityTier: {
              include: {
                entities: true,
              },
            },
          },
        },
      },
    });

    return {
      ...res,
      ...res?.translations[0],
      linkedTickets:
        res?.bookableItems?.flatMap((bi) => [
          ...(bi.ticket ? [bi.ticket] : []),
          ...(bi.entityTier?.entities || []),
        ]) || [],
      id: res?.id,
      trigger: res?.triggerEntity,
    };
  } catch (e) {
    return e;
  }
};
export const getTemplateByIdWithEntities = async (id: string) => {
  try {
    const res = await prisma.design.findUnique({
      where: {
        id,
      },
      include: {
        translations: true,
        invitations: true,
        triggerEntity: {
          include: {
            trigger: true,
          },
        },
        bookableItems: {
          include: {
            ticket: true,
            entityTier: {
              include: {
                entities: true,
              },
            },
          },
        },
      },
    });

    return {
      ...res,
      ...res?.translations[0],
      linkedTickets: res?.bookableItems?.map((bi) => bi.id) || [],
      id: res?.id,
      trigger: res?.triggerEntity,
    };
  } catch (e) {
    return e;
  }
};
export const getDesignTemplateByEventId = async (
  id: string,
  invitationId?: string
) => {
  try {
    let whereClause: any = {
      eventId: id,
      deletedAt: null,
    };
    if (invitationId) {
      whereClause.triggerEntity = {
        some: {
          notification: {
            invitations: {
              some: {
                id: invitationId,
              },
            },
          },
        },
      };
    }

    const res = await prisma.design.findMany({
      where: whereClause,
      include: {
        translations: true,
        invitations: true,
      },
    });

    return res;
  } catch (e: any) {
    console.error(e);
    throw new Error(e.message);
  }
};

// UPDATE
export const updateDesignTemplate = async (
  userId: string,
  template: Design,
  templateTranslations: DesignTemplateTranslations[],
  bookableItemIds?: string[],
  invitationsIds?: string[],
  trigger?: TriggerEvent
) => {
  try {
    const { id, ...rest } = template;
    const design = await prisma.design.findUnique({
      where: { id },
      include: {
        bookableItems: true,
      },
    });
    if (!design) {
      throw new Error("Design template not found");
    }
    const translation = templateTranslations.map(async (t) => {
      const { id, ...rest } = t;
      const res = t.id
        ? await prisma.designTemplateTranslations.update({
            where: {
              id: id,
            },
            data: {
              ...rest,
            },
          })
        : await prisma.designTemplateTranslations.create({
            data: {
              json: t.json,
              html: t.html,
              variables: t.variables,
              language: {
                connect: {
                  id: t.languageId,
                },
              },
              ticketTemplate: {
                connect: {
                  id: template.id,
                },
              },
            },
          });
      return res;
    });

    const res = await prisma.design.update({
      where: {
        id,
      },
      data: {
        ...rest,
        ...(bookableItemIds?.length
          ? {
              bookableItems: {
                connect: bookableItemIds.map((id) => ({ id })),
              },
            }
          : {}),
        ...(invitationsIds?.length
          ? {
              invitations: {
                deleteMany: {},
                createMany: {
                  data: invitationsIds?.map((id) => ({
                    invitationId: id,
                  })),
                },
              },
            }
          : {}),
        updatedAt: new Date(),
      },
    });

    if (trigger) {
      await prisma.trigger.deleteMany({
        where: {
          triggerEntity: {
            entityId: id,
          },
        },
      });
      await prisma.triggerEntity.deleteMany({
        where: {
          entityId: id,
        },
      });
      await createTrigger(
        {
          triggerEvent: trigger,
          createdBy: {
            connect: { id: userId },
          },
          updatedAt: new Date(),
        } as Prisma.TriggerCreateInput,
        TriggerEntityType.DESIGN,
        res.id,
        res.eventId ?? ""
      );
    }

    return { ...res, translation };
  } catch (e) {
    return e;
  }
};

export const duplicateDesignTemplate = async (id: string) => {
  try {
    const design = await prisma.design.findUnique({
      where: { id },
      include: {
        translations: true,
        triggerEntity: {
          include: {
            trigger: true,
          },
        },
        event: true,
        createdBy: true,
      },
    });
    if (!design) {
      throw new Error("Design template not found");
    }

    const newDesign = await createDesignTemplate(
      {
        name: `${design.name} (Copy)`,
        type: design.type ?? DesignTemplateType.DIGITAL_TICKET,
      } as Design,
      design.eventId ?? "",
      design.createdById ?? "",
      [],
      [],
      design.translations?.map((t) => ({
        json: t.json,
        html: t.html,
        languageId: t.languageId,
      })) as DesignTemplateTranslations[],
      design.triggerEntity?.[0]?.trigger?.triggerEvent,
      design.HTMLType ?? undefined
    );

    // Return a plain object with only the necessary properties
    return newDesign;
  } catch (e) {
    throw e;
  }
};

// BACKWARD COMPATIBILITY FUNCTIONS

/**
 * @deprecated Use createDesignTemplate with bookableItemIds instead
 * Legacy function for backward compatibility
 */
export const createDesignTemplateLegacy = async (
  template: Design,
  eventId: string,
  createdById: string,
  ticketsIds?: string[],
  invitationsIds?: string[],
  translations?: DesignTemplateTranslations[],
  trigger?: TriggerEvent,
  HTMLType?: HTMLTemplateType
) => {
  // Convert ticket IDs to BookableItem IDs
  const bookableItemIds = ticketsIds
    ? await getBookableItemIdsFromTicketIds(ticketsIds)
    : [];

  return createDesignTemplate(
    template,
    eventId,
    createdById,
    bookableItemIds,
    invitationsIds,
    translations,
    trigger,
    HTMLType
  );
};

// NEW FUNCTIONS FOR BOOKABLEITEM ARCHITECTURE

/**
 * Attach design to BookableItem (replaces attachDesignToTicket)
 * @param bookableItemId - The BookableItem ID
 * @param designId - The Design ID to attach
 */
export const attachDesignToBookableItem = async (
  bookableItemId: string,
  designId: string
) => {
  try {
    // Update BookableItem.designIds array to include the design
    await prisma.bookableItem.update({
      where: { id: bookableItemId },
      data: {
        designIds: {
          push: designId,
        },
      },
    });
    return true;
  } catch (e) {
    console.error(e);
    throw new Error(`Failed to attach design to BookableItem: ${e}`);
  }
};

/**
 * Detach design from BookableItem
 * @param bookableItemId - The BookableItem ID
 * @param designId - The Design ID to detach
 */
export const detachDesignFromBookableItem = async (
  bookableItemId: string,
  designId: string
) => {
  try {
    // Remove design from BookableItem.designIds array
    const bookableItem = await prisma.bookableItem.findUnique({
      where: { id: bookableItemId },
      select: { designIds: true },
    });

    if (bookableItem) {
      const updatedDesignIds = bookableItem.designIds.filter(
        (id) => id !== designId
      );
      await prisma.bookableItem.update({
        where: { id: bookableItemId },
        data: { designIds: updatedDesignIds },
      });
    }
    return true;
  } catch (e) {
    console.error(e);
    throw new Error(`Failed to detach design from BookableItem: ${e}`);
  }
};

/**
 * Get designs by BookableItem (replaces getDesignsByTicket)
 * @param bookableItemId - The BookableItem ID
 * @param types - Optional filter by design types
 */
export const getDesignsByBookableItem = async (
  bookableItemId: string,
  types?: DesignTemplateType[]
) => {
  try {
    const bookableItem = await prisma.bookableItem.findUnique({
      where: { id: bookableItemId },
      select: { designIds: true },
    });

    if (!bookableItem || !bookableItem.designIds.length) {
      return [];
    }

    const designs = await prisma.design.findMany({
      where: {
        id: { in: bookableItem.designIds },
        deletedAt: null,
        ...(types && { type: { in: types } }),
      },
      include: {
        translations: true,
      },
    });

    return designs.map((design) => ({
      ...design,
      template: design.translations?.[0] ?? null,
    }));
  } catch (e) {
    console.error(e);
    throw new Error(`Failed to get designs for BookableItem: ${e}`);
  }
};

export const softDeleteDesignTemplate = async (id: string) => {
  try {
    // First, remove this design from all BookableItems that have it attached
    const bookableItems = await getBookableItemsByDesign(id);
    bookableItems.length &&
      (await detachDesignFromMultipleBookableItems(
        id,
        bookableItems.map((item) => item.id)
      ));

    await prisma.design.update({
      where: { id },
      data: {
        deletedAt: new Date(),
        invitations: {
          deleteMany: {},
        },
        attachments: {
          deleteMany: {},
        },
        triggerEntity: {
          deleteMany: {},
        },
      },
    });
  } catch (e) {
    console.error(e);
    throw new Error(`Failed to soft delete design template: ${e}`);
  }
};
